import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../services/api";
import PhoneInput, { validatePhone, formatPhone } from "../../components/PhoneInput";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const GENDERS = ["", "Male", "Female", "Other"];

export default function PatientSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkedIn, setCheckedIn] = useState({});
  const [error, setError] = useState("");

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // Delete state
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // weekMode: true when arriving from the "This Week" stat card click
  const weekMode = searchParams.get("week") === "1";

  // weekPatients: results loaded from /patients/week (separate from normal search results)
  const [weekPatients, setWeekPatients]   = useState([]);
  const [weekLoading,  setWeekLoading]    = useState(false);
  const [weekError,    setWeekError]      = useState("");

  // On mount in week mode: auto-fetch this week's patients
  useEffect(() => {
    if (!weekMode) return;
    setWeekLoading(true);
    api.get("/patients/week")
      .then(res => setWeekPatients(res.data || []))
      .catch(() => setWeekError("Could not load this week's patients."))
      .finally(() => setWeekLoading(false));
  }, [weekMode]);

  const search = async () => {
    const q = query.trim();
    if (!q || q.length < 2) { setError("Enter at least 2 characters."); return; }
    setError(""); setLoading(true);
    try {
      const res = await api.get(`/patients/search/${encodeURIComponent(q)}`);
      setResults(res.data);
      if (res.data.length === 0) setError("No patients found.");
    } catch (err) {
      console.error(err); setError("Search failed. Please try again.");
    } finally { setLoading(false); }
  };

  const checkIn = async (p) => {
    try {
      await api.post(`/patients/${p.patientId}/checkin`);
      setCheckedIn((prev) => ({ ...prev, [p._id]: true }));
      setTimeout(() => navigate("/staff-portal/dashboard"), 800);
    } catch (err) { console.error(err); alert("Check-in failed."); }
  };

  const startEdit = (p) => {
    setEditingId(p._id);
    // Parse stored phone string (e.g. "+91 9876543210") back into object for PhoneInput
    const stored = p.phone || "";
    const match = stored.match(/^(\+\d+)\s*(.*)$/);
    const phoneObj = match
      ? { countryCode: match[1], number: match[2] }
      : { countryCode: "+91", number: stored.replace(/\D/g, "").slice(0, 10) };
    setEditForm({ name: p.name, age: p.age || "", gender: p.gender || "", phone: phoneObj, address: p.address || "" });
    setEditError("");
  };

  const saveEdit = async (p) => {
    if (!editForm.name?.trim()) { setEditError("Name is required."); return; }
    setEditSaving(true); setEditError("");
    try {
      const res = await api.put(`/patients/${p._id}`, { ...editForm, phone: formatPhone(editForm.phone) });
      setResults((prev) => prev.map((r) => (r._id === p._id ? res.data : r)));
      setEditingId(null);
    } catch (err) { setEditError(err.response?.data?.message || "Update failed."); }
    finally { setEditSaving(false); }
  };

  const deletePatient = async (p) => {
    setDeleting(true);
    try {
      await api.delete(`/patients/${p._id}`);
      setResults((prev) => prev.filter((r) => r._id !== p._id));
      setConfirmDeleteId(null);
    } catch (err) { alert(err.response?.data?.message || "Delete failed."); }
    finally { setDeleting(false); }
  };

  return (
    <div className="max-w-2xl space-y-5">

      {/* ── Week mode header ── */}
      {weekMode ? (
        <div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h1 className="text-2xl font-bold">This Week's Patients</h1>
            <span style={{
              fontSize: 12, fontWeight: 600,
              background: "#EDE9FE", color: "#5B21B6",
              border: "1px solid #C4B5FD",
              borderRadius: 20, padding: "2px 10px",
            }}>
              📅 Mon – Today
            </span>
          </div>
          <p className="text-sm text-gray-500">
            All patients you saw this week · click any name to view history or check in for today
          </p>
          <button
            onClick={() => navigate("/staff-portal/patient/search")}
            className="text-xs text-blue-500 underline mt-1"
          >
            ← Back to regular search
          </button>
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold">Find Returning Patient</h1>
          <p className="text-sm text-gray-500 mt-1">
            Search by name, phone number, or Patient ID. Use <strong>Check In</strong> to add to today's OPD queue.
          </p>
        </div>
      )}

      {/* ── Week patient list (only in week mode) ── */}
      {weekMode && (
        <div className="space-y-3">
          {weekLoading && (
            <p className="text-sm text-gray-400 text-center py-8">Loading this week's patients…</p>
          )}
          {weekError && (
            <p className="text-sm text-red-500">{weekError}</p>
          )}
          {!weekLoading && !weekError && weekPatients.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <p className="text-3xl mb-2">📋</p>
              <p className="text-sm font-medium">No patients this week yet</p>
            </div>
          )}
          <AnimatePresence>
            {weekPatients.map((p, i) => (
              <motion.div key={p._id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ delay: i * 0.03 }}
                className="card border rounded-xl p-4 bg-white shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800">{p.name}</span>
                      <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{p.patientId}</span>
                      {p.queueDate && (
                        <span style={{
                          fontSize: 10, fontWeight: 600,
                          background: "#EDE9FE", color: "#5B21B6",
                          border: "1px solid #C4B5FD",
                          borderRadius: 20, padding: "1px 7px",
                        }}>
                          {new Date(p.queueDate).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {p.age ? `${p.age} yrs` : "—"} • {p.gender || "—"}
                      {p.phone ? ` • ${p.phone}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                    <button
                      className="btn text-sm px-3"
                      onClick={() => navigate(`/staff-portal/patient/history/${p.patientId}`)}
                    >
                      History
                    </button>
                    <button
                      className="btn-primary text-sm px-3"
                      onClick={() => checkIn(p)}
                    >
                      Check In Today
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Divider before normal search in week mode */}
          {!weekLoading && weekPatients.length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-400 mb-3">Or search for any patient by name / phone / ID below:</p>
            </div>
          )}
        </div>
      )}

      {/* ── Normal search header (hidden in week mode when we have week results) ── */}
      {!(weekMode && !weekLoading && weekPatients.length > 0) && !weekMode && (
        <div>
          {/* header already rendered above */}
        </div>
      )}

      <div className="flex gap-2">
        <input ref={inputRef}
          className="border p-2.5 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          placeholder="Name / Phone / Patient ID"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setError(""); }}
          onKeyDown={(e) => e.key === "Enter" && search()}
          autoFocus />
        <button className="btn-primary px-5" onClick={search} disabled={loading}>
          {loading ? "…" : "Search"}
        </button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <AnimatePresence>
        {results.map((p, i) => (
          <motion.div key={p._id}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            transition={{ delay: i * 0.04 }}
            className="card border rounded-xl p-4 bg-white shadow-sm">

            {editingId === p._id ? (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">✏️ Edit Patient Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Full Name *</label>
                    <input className="border rounded-lg p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Age</label>
                    <input className="border rounded-lg p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      value={editForm.age} onChange={(e) => setEditForm(f => ({ ...f, age: e.target.value.replace(/\D/g,"") }))} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Gender</label>
                    <select className="border rounded-lg p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      value={editForm.gender} onChange={(e) => setEditForm(f => ({ ...f, gender: e.target.value }))}>
                      {GENDERS.map(g => <option key={g} value={g}>{g || "— Select —"}</option>)}
                    </select>
                  </div>
                  <div>
                    <PhoneInput
                      label="Phone"
                      value={editForm.phone}
                      onChange={v => setEditForm(f => ({ ...f, phone: v }))}
                      error={editError && editError.toLowerCase().includes("phone") ? editError : undefined}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">Address</label>
                    <input className="border rounded-lg p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      value={editForm.address} onChange={(e) => setEditForm(f => ({ ...f, address: e.target.value }))} />
                  </div>
                </div>
                {editError && <p className="text-red-500 text-xs">{editError}</p>}
                <div className="flex gap-2 pt-1">
                  <button className="btn-primary text-sm px-4" onClick={() => saveEdit(p)} disabled={editSaving}>
                    {editSaving ? "Saving…" : "Save Changes"}
                  </button>
                  <button className="btn text-sm px-4" onClick={() => setEditingId(null)} disabled={editSaving}>Cancel</button>
                </div>
              </div>

            ) : confirmDeleteId === p._id ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-red-700">
                  ⚠️ Delete <strong>{p.name}</strong> ({p.patientId})? This cannot be undone and will remove all their records.
                </p>
                <div className="flex gap-2">
                  <button className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
                    onClick={() => deletePatient(p)} disabled={deleting}>
                    {deleting ? "Deleting…" : "Yes, Delete"}
                  </button>
                  <button className="btn text-sm px-4" onClick={() => setConfirmDeleteId(null)} disabled={deleting}>Cancel</button>
                </div>
              </div>

            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800">{p.name}</span>
                    <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{p.patientId}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {p.age ? `${p.age} yrs` : "—"} • {p.gender || "—"}
                    {p.phone ? ` • ${p.phone}` : ""}
                    {p.address ? ` • ${p.address}` : ""}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap justify-end">
                  <button className="btn text-sm px-3" onClick={() => navigate(`/staff-portal/patient/history/${p.patientId}`)}>History</button>
                  <button className="btn text-sm px-3 border border-blue-300 text-blue-600 hover:bg-blue-50" onClick={() => startEdit(p)}>✏️ Edit</button>
                  <button className="btn text-sm px-3 border border-red-300 text-red-600 hover:bg-red-50" onClick={() => setConfirmDeleteId(p._id)}>🗑️ Delete</button>
                  {checkedIn[p._id] ? (
                    <span className="text-sm text-green-600 font-medium px-3 py-1.5">✓ Checked In</span>
                  ) : (
                    <button className="btn-primary text-sm px-3" onClick={() => checkIn(p)}>Check In Today</button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      <div className="pt-2 border-t text-sm text-gray-500">
        Patient not found?{" "}
        <button className="text-blue-600 underline" onClick={() => navigate("/staff-portal/patient/new")}>
          Create new patient
        </button>
      </div>
    </div>
  );
}